import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useEffect, useState } from 'react';
import MoneyLabel from '../label/MoneyLabel';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RenderHTML from '../preview/RenderHTML';
import { numberToString } from './numberConverter';
import EditIcon from '@mui/icons-material/Edit';
import DateLabel from '../label/DateLabel';
import { renderHours } from '../../scenes/time-tracking/worktime';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

export const BillingPosition = ({
  position,
  onEdit,
  onSelectionChange,
  selection,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  const {
    type,
    name,
    description,
    unit,
    amount,
    taxRate,
    price,
    image,
    workPackages,
    totalTimeBilledSeconds,
    totalTimeWorkedSeconds,
    costBreakdown,
    totalCost,
    serviceId,
    service,
  } = position;

  const workPackageCount = workPackages.length;

  const iconMap = {
    time: AccessTimeIcon,
    flat: AttachMoneyIcon,
  };

  const billableSeconds = totalTimeWorkedSeconds - totalTimeBilledSeconds;
  const hasOpenTime = billableSeconds > 0;

  const priceMap = {
    time: (
      <Box>
        <Typography>
          <span style={{ fontSize: 10 }} className="mr-1 opacity-50">
            {t('open')}
          </span>
          <span className="monospace" style={{ fontWeight: 500 }}>
            <MoneyLabel money={(billableSeconds / 60 / 60) * price} />
          </span>
        </Typography>
        <Typography className="opacity-50">
          <MoneyLabel money={price} />
          <span style={{ fontSize: 10 }} className="ml-1 ">
            {`/ ${t('hour')}`}
          </span>
        </Typography>
      </Box>
    ),
    flat: (
      <Box>
        <Typography className="hidden">
          <span style={{ fontSize: 10 }} className="mr-1 opacity-50">
            {t('open')}
          </span>
          <span className="monospace" style={{ fontWeight: 500 }}>
            <MoneyLabel money={(billableSeconds / 60 / 60) * price} />
          </span>
        </Typography>
        <Typography>
          <MoneyLabel money={price} />
          {unit && (
            <span style={{ fontSize: 10 }} className="ml-1 opacity-50">
              {` / ${unit}`}
            </span>
          )}
        </Typography>
      </Box>
    ),
  };

  const Icon = iconMap[type];
  const priceLabel = priceMap[type];

  // select logic
  function countSelectedItems() {
    var selectedCount = 0;
    workPackages.forEach((wp) => {
      if (selection.includes(wp.id)) selectedCount++;
    });
    return selectedCount;
  }

  useEffect(() => {
    if (selection) setSelectedCount(countSelectedItems());
  }, [selection]);

  // If no open time, always show unchecked
  const SelectedIcon = !hasOpenTime
    ? CheckBoxOutlineBlankIcon // no open time - always unchecked
    : selectedCount === workPackageCount
    ? CheckBoxIcon // all selected
    : selectedCount === 0
    ? CheckBoxOutlineBlankIcon // nothing selected
    : IndeterminateCheckBoxIcon; // partially selected

  function handleSelectedWorkPackage(wp) {
    // Check if WP has open time before allowing selection
    const wpWorked = wp.workedTimeSeconds || wp.totalTimeWorkedSeconds || wp.total_time_worked_seconds || 0;
    const wpBilled = wp.billedTimeSeconds || wp.totalTimeBilledSeconds || wp.total_time_billed_seconds || 0;
    const wpHasOpenTime = wpWorked - wpBilled > 0;

    if (!wpHasOpenTime) return; // Don't allow selection if no open time

    if (selection.includes(wp.id)) {
      onSelectionChange(selection.filter((wpId) => wpId !== wp.id));
    } else {
      onSelectionChange([...selection, wp.id]);
    }
  }

  function handleMainSelect(e) {
    e.stopPropagation();

    // Don't allow selection if no open time
    if (!hasOpenTime) return;

    // Only select/deselect WPs that have open time
    const selectableWPs = workPackages.filter((wp) => {
      const wpWorked = wp.workedTimeSeconds || wp.totalTimeWorkedSeconds || wp.total_time_worked_seconds || 0;
      const wpBilled = wp.billedTimeSeconds || wp.totalTimeBilledSeconds || wp.total_time_billed_seconds || 0;
      return wpWorked - wpBilled > 0;
    });

    const selectableIds = selectableWPs.map((wp) => wp.id);
    const currentlySelectedCount = selection.filter((id) => selectableIds.includes(id)).length;

    // remove all items from selection
    if (currentlySelectedCount === selectableIds.length) {
      const idsToRemove = new Set(selectableIds);
      return onSelectionChange(selection.filter((id) => !idsToRemove.has(id)));
    }

    // add all selectable items to selection
    const newSelection = Array.from(new Set([...selection, ...selectableIds]));
    return onSelectionChange(newSelection);
  }

  return (
    <Box
      sx={{
        bgcolor: colors.bgInput,
        border: `1px solid ${colors.selected}`,
        // Grey out if no open time
        opacity: hasOpenTime ? 1 : 0.5,
        '.edit-button': {
          opacity: 0,
          transform: 'scale(0)',
          transition: '0.3s ease',
        },
        ':hover .edit-button': {
          opacity: 1,
          transform: 'scale(1)',
        },
      }}
      className="flex flex-col w-full rounded-lg"
    >
      <Button
        onClick={() => setOpen(!open)}
        className="px-2 py-2 flex items-center gap-2 min-h-[56px]"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Box className="flex items-center gap-2">
          {onSelectionChange && (
            <IconButton onClick={handleMainSelect}>
              {SelectedIcon && <SelectedIcon />}
            </IconButton>
          )}
          {image ? (
            <img
              className="w-[40px] h-[40px] rounded-md object-cover"
              src={image.temporaryUrl}
            />
          ) : (
            <Icon />
          )}

          <Typography fontWeight={500}>
            {name}
            <span className="opacity-50 ml-2" style={{ fontSize: 10 }}>
              {workPackageCount > 0
                ? `[${workPackageCount} ${
                    workPackageCount === 1
                      ? t('Work Package')
                      : t('Work Packages')
                  }]`
                : t('No Work Package')}
            </span>
          </Typography>
          {serviceId && (
            <Chip
              size="small"
              label={service?.nr || t('Service')}
              icon={<SupervisedUserCircleIcon fontSize="small" />}
              sx={{
                height: 20,
                bgcolor: colors.info + '20',
                color: colors.info,
                border: 'none',
                '& .MuiChip-label': { fontSize: 10 },
                '& .MuiChip-icon': { color: colors.info },
              }}
            />
          )}
          {onEdit && (
            <IconButton
              onClick={() => onEdit(position)}
              className="edit-button"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Box className="flex gap-1">
          {priceLabel}
          <ArrowDropDownIcon
            sx={{ transform: `rotate(${open ? '180deg' : '0deg'})` }}
          />
        </Box>
      </Button>
      <Collapse in={open}>
        <Box className="flex flex-col px-4 pb-2">
          <Box className="flex gap-4 flex-row-reverse">
            {amount && (
              <Box>
                <Typography fontSize={10} className="opacity-50">
                  {t('Amount')}
                </Typography>
                <Typography className="monospace">
                  {`${numberToString(amount, 2)} ${unit || ''}`}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography fontSize={10} className="opacity-50">
                {t('Tax Rate')}
              </Typography>
              <Typography className="monospace">
                {numberToString(taxRate, 0)} %
              </Typography>
            </Box>
          </Box>
          {description && (
            <Box>
              <Typography fontSize={10} className="opacity-50">
                {t('Description')}
              </Typography>
              <RenderHTML HTML={description} />
            </Box>
          )}
          {workPackageCount > 0 && (
            <Box className="flex flex-col w-full gap-2">
              <Typography fontSize={10} className="opacity-50">
                {t('Work Packages')}
              </Typography>
              {workPackages.map((wp) => (
                <WPPreview
                  key={wp.id}
                  workPackage={wp}
                  onSelect={
                    onSelectionChange ? handleSelectedWorkPackage : null
                  }
                  isSelected={selection?.includes(wp.id)}
                />
              ))}
            </Box>
          )}
          {costBreakdown && costBreakdown.length > 0 && (
            <CostBreakdownDisplay
              costBreakdown={costBreakdown}
              totalCost={totalCost}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const WPPreview = ({ workPackage, onSelect, isSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const { title, nr, estimatedTime, startDate, endDate, isBillable, icon } =
    workPackage;

  if (!Boolean(isBillable)) return;

  // Calculate unbilled time - check both camelCase and snake_case properties
  const workedSeconds = workPackage.workedTimeSeconds || workPackage.totalTimeWorkedSeconds || workPackage.total_time_worked_seconds || 0;
  const billedSeconds = workPackage.billedTimeSeconds || workPackage.totalTimeBilledSeconds || workPackage.total_time_billed_seconds || 0;
  const hasOpenTime = workedSeconds - billedSeconds > 0;

  // If no open time, show unchecked icon and disable selection
  const SelectedIcon = !hasOpenTime
    ? CheckBoxOutlineBlankIcon // no open time - always show unchecked
    : isSelected
    ? CheckBoxIcon // selected
    : CheckBoxOutlineBlankIcon; // nothing selected

  // Handler only works if there's open time
  const handleClick = () => {
    if (hasOpenTime && onSelect) {
      onSelect(workPackage);
    }
  };

  return (
    <Box
      className="rounded-md px-2 py-1 flex justify-between cursor-pointer"
      sx={{
        border: `1px solid ${
          isSelected && hasOpenTime ? `${colors.info}80` : colors.selected
        }`,
        bgcolor: isSelected && hasOpenTime ? colors.info + '30' : 'transparent',
        // Grey out if no open time
        opacity: hasOpenTime ? 1 : 0.5,
        cursor: hasOpenTime ? 'pointer' : 'default',
        ':hover': onSelect && hasOpenTime
          ? {
              border: `1px solid ${colors.info}80`,
              bgcolor: colors.info + '30',
            }
          : {},
      }}
      onClick={handleClick}
    >
      <Box className="flex gap-2 items-center">
        {onSelect && <SelectedIcon sx={{ opacity: hasOpenTime ? 1 : 0.5 }} />}
        {icon && (
          <img
            src={icon?.temporaryUrl}
            className="w-[36px] h-[36px] object-contain"
          />
        )}
        <Box className="flex flex-col">
          <Typography>
            <span className="monospace mr-2 opacity-50">{nr}</span>
            {title}
          </Typography>
          <Typography fontSize={10} className="opacity-50">
            <DateLabel date={startDate} />
            {` ${t('until')} `}
            <DateLabel date={endDate} />
          </Typography>
        </Box>
      </Box>
      <Typography className="monospace">
        {`${renderHours(workedSeconds / 60)} h`}
        {estimatedTime && (
          <span className="ml-2 opacity-50" style={{ fontSize: 10 }}>
            {`/ ${renderHours(estimatedTime / 60)} h`}
          </span>
        )}
      </Typography>
    </Box>
  );
};

const CostBreakdownDisplay = ({ costBreakdown, totalCost }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  if (!costBreakdown || costBreakdown.length === 0) return null;

  return (
    <Box className="flex flex-col w-full gap-2 mt-2">
      <Typography fontSize={10} className="opacity-50">
        {t('Cost Breakdown')}
      </Typography>
      <Box className="flex flex-col gap-1">
        {costBreakdown.map((breakdown, index) => (
          <Box
            key={breakdown.wage_preset_id || `unassigned-${index}`}
            className="rounded-md px-2 py-1 flex justify-between items-center"
            sx={{
              backgroundColor: `${breakdown.wage_preset_color}30`,
              border: `1px solid ${breakdown.wage_preset_color}80`,
            }}
          >
            <Box className="flex gap-2 items-center">
              {breakdown.has_warning && (
                <Tooltip title={breakdown.warning_message || t('No wage preset assigned')}>
                  <WarningAmberIcon
                    sx={{ color: colors.warning[500], fontSize: 18 }}
                  />
                </Tooltip>
              )}
              <Typography fontSize={12}>
                {breakdown.wage_preset_label}
              </Typography>
              {breakdown.users && breakdown.users.length > 0 && (
                <Typography fontSize={10} className="opacity-50">
                  ({breakdown.users.map((u) => `${u.first_name} ${u.last_name}`).join(', ')})
                </Typography>
              )}
            </Box>
            <Box className="flex gap-2 items-center">
              <Typography fontSize={12} className="monospace">
                {numberToString(breakdown.hours, 2)} {t('hours')}
              </Typography>
              {breakdown.wage !== null && (
                <>
                  <Typography fontSize={10} className="opacity-50">
                    @
                  </Typography>
                  <Typography fontSize={12} className="monospace">
                    <MoneyLabel money={breakdown.wage} />
                  </Typography>
                </>
              )}
              <Typography fontSize={10} className="opacity-50">
                =
              </Typography>
              <Typography fontSize={12} fontWeight={500} className="monospace">
                <MoneyLabel money={breakdown.total_cost} />
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {totalCost !== undefined && (
        <Box className="flex justify-end">
          <Typography fontSize={12} fontWeight={600} className="monospace">
            {t('Sum')}: <MoneyLabel money={totalCost} />
          </Typography>
        </Box>
      )}
    </Box>
  );
};
